import { ApiClient, ApiResp } from "../../api/ApiClient";

const apiClient = ApiClient.getDefault();

export interface Manual {
  id: number;
  slug: string | null;
  title: string;
  summary: string | null;
  categoryId: number;
  htmlContent: string;
  versionNo: number;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
  updatedBy: number | null;
}

export interface ManualCategory {
  id: number;
  name: string;
  sortOrder: number;
}

export interface ManualSearchResult {
  manualId: number;
  title: string;
  slug: string;
  categoryId: number;
  categoryName: string;
  summary: string;
  snippet: string;
  score: number;
}

export interface ManualSaveRequest {
  id?: number;
  slug?: string;
  title: string;
  summary?: string;
  categoryId: number;
  htmlContent: string;
  changeNote?: string;
}

export interface ManualVersionInfo {
  id: number;
  manualId: number;
  versionNo: number;
  changeNote: string | null;
  createdBy: number | null;
  createdAt: string;
}

export interface ManualAiGenerateRequest {
  description: string;
  style: "steps" | "flow" | "comparison" | "general";
}

export interface ManualAiGenerateResult {
  html: string;
  model: string;
  provider: string;
  promptTokens: number;
  completionTokens: number;
  latencyMs: number;
}

export interface ManualAsset {
  id: number;
  manualId: number;
  assetNo: number;
  ossUrl: string;
  originalFilename: string;
  contentType: string | null;
  sizeBytes: number | null;
  createdAt: string;
}

// 内部辅助：发起请求并解包 .data
async function get<T>(path: string, params?: Record<string, any> | null): Promise<T> {
  const resp = await apiClient.request<T>(path, "GET", params ?? undefined);
  return resp.data;
}
async function post<T>(path: string, dto?: any): Promise<T> {
  const resp = await apiClient.request<T>(path, "POST", undefined, dto);
  return resp.data;
}
async function put<T>(path: string, dto?: any): Promise<T> {
  const resp = await apiClient.request<T>(path, "PUT", undefined, dto);
  return resp.data;
}
async function del<T>(path: string): Promise<T> {
  const resp = await apiClient.request<T>(path, "DELETE");
  return resp.data;
}

export const ManualApi = {
  categoryList: () => get<ManualCategory[]>("/manual/category/list"),
  categoryCreate: (data: Partial<ManualCategory>) => post<ManualCategory>("/manual/category", data),
  categoryUpdate: (id: number, data: Partial<ManualCategory>) => put<ManualCategory>(`/manual/category/${id}`, data),
  categoryDelete: (id: number) => del<void>(`/manual/category/${id}`),

  list: (categoryId?: number) => get<Manual[]>("/manual/list", categoryId != null ? { categoryId } : null),
  search: (keyword: string, categoryId?: number) =>
    get<ManualSearchResult[]>("/manual/search", { keyword, categoryId, size: 20 }),
  get: (idOrSlug: string | number) => get<Manual>(`/manual/${idOrSlug}`),
  create: (data: ManualSaveRequest) => post<Manual>("/manual", data),
  update: (id: number, data: ManualSaveRequest) => put<Manual>(`/manual/${id}`, data),
  remove: (id: number) => del<void>(`/manual/${id}`),

  versions: (id: number) => get<ManualVersionInfo[]>(`/manual/${id}/versions`),
  versionContent: (id: number, vno: number) =>
    get<{ id: number; htmlContent: string }>(`/manual/${id}/versions/${vno}`),
  rollback: (id: number, vno: number) => post<void>(`/manual/${id}/rollback/${vno}`),

  aiGenerate: (data: ManualAiGenerateRequest) =>
    post<ManualAiGenerateResult>("/manual/ai/generate", data),

  renderUrl: (slug: string) => `/api/mis/manual/render/${slug}`,

  templates: () => get<Manual[]>("/manual/templates"),
  fromTemplate: (templateId: number) =>
    post<Manual>(`/manual/from-template/${templateId}`),
  cloneAsTemplate: (id: number, title: string) =>
    post<Manual>(`/manual/clone-as-template/${id}?title=${encodeURIComponent(title)}`),

  listAssets: (manualId: number) => get<ManualAsset[]>(`/manual/${manualId}/assets`),
  deleteAsset: (manualId: number, assetNo: number) =>
    del<void>(`/manual/${manualId}/assets/${assetNo}`),
  uploadAssetsBatch: async (manualId: number, files: File[]): Promise<ManualAsset[]> => {
    const fd = new FormData();
    files.forEach(f => fd.append("files", f));
    const resp = await apiClient.request<any>(
      `/manual/${manualId}/assets/batch`,
      "POST",
      undefined,
      fd
    );
    return resp.data as ManualAsset[];
  },
};
